'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useSession } from '../auth/client';

interface NegotiatorModeContextType {
  isNegotiatorMode: boolean;
  setNegotiatorMode: (enabled: boolean) => void;
  toggleNegotiatorMode: () => void;
}

const NegotiatorModeContext = createContext<NegotiatorModeContextType | undefined>(undefined);

interface NegotiatorModeProviderProps {
  children: ReactNode;
}

export function NegotiatorModeProvider({ children }: NegotiatorModeProviderProps) {
  const { data: session } = useSession();
  const user = session?.user;
  const userId = user?.id;
  const [isNegotiatorMode, setIsNegotiatorMode] = useState(false);

  // Get user-specific localStorage key
  const getStorageKey = useCallback(() => {
    if (!userId) return null;
    return `negotiator_mode_${userId}`;
  }, [userId]);

  // Load from localStorage on mount (user-specific)
  useEffect(() => {
    const key = getStorageKey();
    if (key) {
      const saved = localStorage.getItem(key);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs mode from localStorage (external system)
      setIsNegotiatorMode(saved === 'true');
    }
  }, [getStorageKey]);

  // Save to localStorage when changed (user-specific)
  const setNegotiatorMode = useCallback(
    (enabled: boolean) => {
      setIsNegotiatorMode(enabled);
      const key = getStorageKey();
      if (key) {
        localStorage.setItem(key, enabled ? 'true' : 'false');
      }
    },
    [getStorageKey]
  );

  const toggleNegotiatorMode = useCallback(() => {
    setNegotiatorMode(!isNegotiatorMode);
  }, [isNegotiatorMode, setNegotiatorMode]);

  return (
    <NegotiatorModeContext.Provider
      value={{
        isNegotiatorMode,
        setNegotiatorMode,
        toggleNegotiatorMode,
      }}
    >
      {children}
    </NegotiatorModeContext.Provider>
  );
}

export function useNegotiatorMode() {
  const context = useContext(NegotiatorModeContext);
  if (context === undefined) {
    throw new Error('useNegotiatorMode must be used within a NegotiatorModeProvider');
  }
  return context;
}
